import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom"; // Import Link and useLocation
import { Menu, X, Mail, MapPin, Phone, Globe, ArrowRight } from "lucide-react";
import TirumalaLogo from "../assets/coco.jpeg";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const location = useLocation(); // Get the current location

  // Logo placeholder - replace with your actual logo
  const navLinks = [
    { title: "Home", path: "/", description: "Welcome to Tirumala Cocopeat" },
    { title: "About", path: "/aboutus", description: "Our story and mission" },
    { title: "Products", path: "/products", description: "Premium cocopeat solutions" },
    { title: "Process", path: "/process", description: "Manufacturing excellence" },
    { title: "Contact", path: "/contact", description: "Get in touch with us" },
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Use the current path from `useLocation` to determine the active link
  const activeLink = location.pathname;

  return (
    <div>
      {/* Top Contact Bar */}
      <div className="bg-gradient-to-r from-green-800 to-emerald-700 text-white py-2 px-4 hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2 hover:text-green-200 transition-colors">
              <Mail className="w-4 h-4" />
              <span>mramana918@gmail.com</span>
            </div>
            <div className="flex items-center space-x-2 hover:text-green-200 transition-colors">
              <MapPin className="w-4 h-4" />
              <span>Anantapur, Kalyanadurgam-515761</span>
            </div>
            <div className="flex items-center space-x-2 hover:text-green-200 transition-colors">
              <Globe className="w-4 h-4" />
              <span>Exporting Worldwide</span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-xs font-medium">Follow Us:</span>
            <div className="flex space-x-3">
              <a
                href="https://facebook.com"
                className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center hover:bg-blue-600 hover:scale-110 transition-all duration-300"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="text-xs">f</span>
              </a>
              <a
                href="https://instagram.com"
                className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center hover:bg-pink-600 hover:scale-110 transition-all duration-300"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="text-xs">i</span>
              </a>
              <a
                href="https://twitter.com"
                className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center hover:bg-blue-400 hover:scale-110 transition-all duration-300"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="text-xs">t</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`fixed top-0 left-0 w-full z-40 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-xl border-b border-gray-200' 
          : 'bg-white shadow-md'
      } ${isScrolled ? 'mt-0' : 'md:mt-10'}`}>
        
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            
            {/* Enhanced Logo Section */}
            <div className="flex items-center space-x-4">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                <Link
                  to="/" // Use Link here
                  className="relative flex items-center space-x-3 p-2 rounded-xl transition-all duration-300 hover:bg-green-50"
                >
                  <img
                    className="h-12 w-12 rounded-lg shadow-md"
                    src={TirumalaLogo}
                    alt="Tirumala Cocopeat"
                  />
                  <div className="lg:block">
                    <h1 className="text-xl font-bold text-gray-900">Tirumala Cocopeat</h1>
                    <p className="text-xs text-green-600 font-medium">Premium Quality Products</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Desktop Navigation with Enhanced Hover Effects */}
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link, index) => (
                <div key={link.title} className="relative group">
                  <Link // Use Link here
                    to={link.path}
                    className={`relative px-4 py-3 text-sm font-medium transition-all duration-300 rounded-lg
                      ${activeLink === link.path
                        ? 'text-green-600 bg-green-50'
                        : 'text-gray-700 hover:text-green-600 hover:bg-green-50'
                      }`}
                    onMouseEnter={() => setHoveredLink(index)}
                    onMouseLeave={() => setHoveredLink(null)}
                  >
                    <span className="relative z-10">{link.title}</span>
                    {activeLink === link.path && (
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-green-500 rounded-full"></div>
                    )}
                  </Link>
                  
                  {/* Enhanced Tooltip */}
                  {hoveredLink === index && (
                    <div className="absolute top-full mt-2 left-1 -translate-x-1/2 px-3 py-2 bg-gray-900 text-white text-xs rounded-md shadow-lg z-20 whitespace-nowrap pointer-events-none transition-all duration-300 animate-fadeIn">
                      <div className="absolute -top-1 left-1/4 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45 z-10"></div>
                      <span className="relative z-20">{link.description}</span>
                    </div>

                  )}
                </div>
              ))}
            </div>

            {/* Enhanced CTA Section */}
            <div className="hidden lg:flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-gray-600 group">
                <Phone className="w-4 h-4 text-green-500 group-hover:scale-110 transition-transform" />
                <a href="tel:+919492228585" className="font-medium hover:text-green-600 transition-colors">
                  +91 9492228585
                </a>
              </div>
              <Link
                to="/contact"
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-medium hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center space-x-2"
              >
                <span>Get Quote</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-3 rounded-xl text-gray-700 hover:bg-gray-100 transition-all duration-300 hover:scale-105"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Mobile Menu */}
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
              onClick={() => setIsMenuOpen(false)}
            ></div>
            
            {/* Mobile Menu Panel */}
            <div className="absolute top-full left-0 right-0 bg-white shadow-2xl border-t lg:hidden z-40">
              <div className="px-4 py-6 space-y-4">
                
                {/* Mobile Contact Info */}
                <div className="border-b border-gray-200 pb-4 mb-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <img src={TirumalaLogo} alt="Logo" className="w-10 h-10 rounded-lg" />
                    <div>
                      <h3 className="font-bold text-gray-900">Tirumala Cocopeat</h3>
                      <p className="text-sm text-green-600">Premium Quality Products</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4 text-green-500" />
                      <span>mramana918@gmail.com</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-green-500" />
                      <span>+91 9492228585</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-green-500" />
                      <span>Anantapur, Kalyanadurgam</span>
                    </div>
                  </div>
                </div>

                {/* Mobile Navigation Links */}
                <div className="space-y-2">
                  {navLinks.map((link, index) => (
                    <Link
                      key={link.title}
                      to={link.path}
                      onClick={() => setIsMenuOpen(false)} // Close menu on click
                      className={`w-full text-left px-4 py-4 rounded-xl transition-all duration-300 flex items-center justify-between group
                        ${activeLink === link.path
                          ? 'bg-green-50 text-green-600 border border-green-200'
                          : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      style={{animationDelay: `${index * 0.1}s`}}
                    >
                      <div>
                        <div className="font-medium">{link.title}</div>
                        <div className="text-sm text-gray-500">{link.description}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  ))}
                </div>

                {/* Mobile CTA */}
                <div className="pt-4 border-t border-gray-200">
                  <Link
                    to="/contact"
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 rounded-xl font-medium hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg flex items-center justify-center space-x-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span>Get Free Quote</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Mobile Social Links */}
                <div className="flex justify-center space-x-4 pt-4">
                  <a href="https://www.facebook.com/tirumalacocopeat" className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
                    <span className="text-sm">f</span>
                  </a>
                  <a href="https://www.instagram.com/tirumalacocopeat" className="w-10 h-10 bg-pink-600 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
                    <span className="text-sm">i</span>
                  </a>
                  <a href="https://twitter.com/tirumalacocopeat" className="w-10 h-10 bg-blue-400 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
                    <span className="text-sm">t</span>
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
      </nav>

      {/* Spacer to prevent content overlap */}
      <div className={`${isScrolled ? 'h-5' : 'h-5 md:h-16'} transition-all duration-300`}></div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Navbar;